<template>
    <div class="webcam-container" :style="{width:width+'px',height:height+'px'}">
      <BrowserProctoringMonitor v-if="enforceToStayInBrowser && (active || currentCountdown!==null)" @issue="proctoringIssueTriggered()"></BrowserProctoringMonitor>
      <transition-group name="fade">
            <video key="video" ref="video" :width="width" :height="height" autoplay muted playsinline :class="{'blur':blurWebcam}"/>
            <div key="overlay" class="webcam-window">
                <RecordDot v-if="active"></RecordDot>
                <div v-if="loading && countdown!==null" class="overlay-center">{{$t('components.video_record.get_ready')}}</div>
                <div v-else-if="currentCountdown!==null && countdown!==null" class="overlay-center" style="font-size:70pt">{{currentCountdown}}</div>
                <RecordingError v-if="error" :error="error" @dismiss="error=null" :small="small"></RecordingError>

                <div style="position:absolute; bottom: 10px; left:10px; width: 60px; align-items: center; display: flex"
                    v-if="!uploading && !uploadSuccess && !uploadError"
                >
                    <v-icon :size="20" :color="micIsActive ? 'white' : 'red'">mdi-microphone-outline</v-icon>
                    <v-progress-linear :value="micLevel" color="white" style="margin-left: 3px"></v-progress-linear>
                </div>

                <transition-group name="fade">
                    <div key="uploading" v-if="uploading" class="overlay-center">
                        <div v-if="uploadPercent!==1">
                            {{$t('components.video_record.confirm.uploading')}}
                            <div style="text-align: center"><v-progress-circular :value="uploadPercent*100" color="white"></v-progress-circular></div>
                        </div>
                        <div v-else>
                            {{$t('components.video_record.confirm.confirming')}}
                            <div style="text-align: center"><v-progress-circular indeterminate color="white"></v-progress-circular></div>
                        </div>

                    </div>
                    <div key="upload-success" v-else-if="uploadSuccess" class="overlay-center">
                        <div>
                            {{$t('components.video_record.confirm.success')}}
                            <br>
                            <div style="text-align: center">
                                <v-icon :size="height*0.3">mdi-check-circle-outline</v-icon>
                            </div>
                        </div>
                    </div>
                    <div key="upload-error" v-else-if="uploadError" class="overlay-center" style="text-align: center; font-size:20pt">
                        <div>
                            <div style="text-align: center">
                                <v-icon :size="height*0.1">mdi-alert-circle-outline</v-icon>
                            </div>
                            <div>
                                {{$t('components.video_record.confirm.error_title')}}
                            </div>
                            <div style="font-size:12pt; font-weight: normal; padding: 5px">
                                {{ uploadError }}
                            </div>
                            <div style="text-align: center; margin-top:15px;">
                                <v-btn @click="reset()">{{$t('components.video_record.confirm.record_new')}}</v-btn>
                                <v-btn @click="uploadBlobs()" style="margin-left:5px" v-if="canRetryUpload">{{$t('components.video_record.confirm.upload_again')}}</v-btn>
                            </div>
                        </div>
                    </div>
                    <div key="mic-monitor" class="overlay-center" v-else-if="!micIsActive && active">
                       <div>
                           <div style="text-align: center; font-size:15pt;">
                               {{$t('components.checks.mic.nohear')}}
                           </div>
                           <div style="text-align: center; margin-top:15px;">
                               <v-btn @click="stopAndOpenSettings()">{{$t('components.video_record.open_settings')}}</v-btn>
                           </div>
                       </div>
                    </div>
                    <div key="codecs-unsupported" class="overlay-center" v-else-if="!codecSupported">
                        <div>
                            <div style="text-align: center; font-size:12pt;">
                                {{$t('components.video_record.codec_not_supported')}}
                            </div>
                            <div style="text-align: center; margin-top:15px;">
                                <v-btn @click="codecSupported=true">{{$t('components.video_record.continue_anyway')}}</v-btn>
                            </div>
                        </div>
                    </div>
                </transition-group>
            </div>
        </transition-group>
    </div>
</template>

<script>
    import {configureDevices, stopMicLevelCheck, micLevelCheck} from "../../lib/mediaDevices";
    import RecordRTC from 'recordrtc';
    import soundController from "../../lib/soundController";
    import audioInputMixin from "../settings/audioInputMixin";
    import RecordDot from "../helpers/RecordDot";
    import RecordingError from "./components/RecordingError";
    import {v4 as uuidv4} from "uuid";
    import BrowserProctoringMonitor from "../measure/question/elements/BrowserProctoringMonitor.vue";
    export default {
        name: "LocalWebcam",
        components:{
          BrowserProctoringMonitor,
            RecordDot,
            RecordingError
        },
        props: {
            recordingWidth:{
                default:640
            },
            width:{
                default:640
            },
            height:{
                default:480
            },
            countdown:{
                default:null
            },
            blur:{
                default:false
            },
            minimumRecordingSecs:{
                default:5
            },
            assessmentanswerid:{
                required:true,
                default:null
            },
            assessmentid:{
                required:true,
                default:null
            },
            enforceToStayInBrowser: {
              type:Boolean,
              default:false
            }
        },
        mixins:[audioInputMixin],
        data(){
            return {
                loading : false,
                active: false,
                tracks : [],
                stream : null,
                currentCountdown:null,
                error:null,
                countdownInterval:null,
                recordingInterval:null,
                currentRecordingSeconds:0,
                localAudioRecording: null,
                localVideoRecording : null,
                micIsActive:true,
                micLevel: 0,
                codecSupported: true,
                //Upload variables
                blobsToUpload : null,
                uploading : false,
                responsevideo:null,
                videofile : null,
                uploadPercent:0,
                audioUploadComplete:false,
                videoUploadComplete:false,
                uploadError:null,
                uploadSuccess:false,
                canRetryUpload:false,
                //permissions
                cameraPermissionStatus:null,
                micPermissionStatus:null,
            }
        },
        computed : {
            blurWebcam(){
               return this.blur || this.uploading || this.uploadSuccess || this.uploadError;
            },
            small(){
                return this.width < 640;
            },
            cameraDevice(){
                return this.$store.state.media.cameraDevice;
            },
            audioInputDevice(){
                return this.$store.state.media.audioInputDevice;
            },
            audioOutputDevice(){
                return this.$store.state.media.audioOutputDevice;
            },
            settings(){
                let audio = true;
                let video = { width: this.recordingWidth, facingMode: 'user'};
                if(this.cameraDevice){
                    video['deviceId']=this.cameraDevice;
                }
                if(this.audioInputDevice){
                    audio = {
                        deviceId:this.audioInputDevice,
                        echoCancellation: true
                    }
                }
                return {
                    audio,video
                };
            },
            audioRecordRTCSettings(){
                let audioMime = 'audio/wav';

                if(MediaRecorder.isTypeSupported('audio/webm;codecs=opus')){
                    audioMime = 'audio/webm;codecs=opus';
                }

                return {
                    type: 'audio',
                    mimeType: audioMime,
                    audioBitsPerSecond: 32000,
                    timeSlice: 2000,
                    numberOfAudioChannels: 1,
                    disableLogs: true
                };
            },
            videoRecordRTCSettings(){
                let videoMime = 'video/mp4';
                if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
                    videoMime = 'video/webm;codecs=vp8,opus';
                }
                return  {
                    type: 'video',
                    video: {
                        width: this.width,
                        height: this.height,
                        frameRate: 24,
                    },
                    mimeType: videoMime,
                    audioBitsPerSecond: 32000,
                    videoBitsPerSecond: 128000 / 2,
                    timeSlice: 2000,
                    numberOfAudioChannels: 1,
                    disableLogs: true
                };
            },
            hasReachedMinimumRecordTime(){
              return this.currentRecordingSeconds >= this.minimumRecordingSecs;
            },
            assessment(){
                return this.$store.state.assessment.assessment;
            },
        },
        watch : {
            cameraDevice(){
                this.onDeviceUpdated();
            },
            audioInputDevice(){
                this.onDeviceUpdated();
            }
        },
        methods: {
            determineCodecSupport(){
                this.codecSupported = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') && MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus');
            },
            checkDevicesInStore(){
                this.listenForDeviceChanges();
                return configureDevices();
            },
            listenForDeviceChanges(){
                if(this.deviceChangeListener){
                    navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeListener);
                }

                this.deviceChangeListener = () => {
                    this.handleDeviceChanges();
                };
                navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeListener);
                this.clearPermissionListeners();
                navigator.permissions.query({ name: 'camera' }).then((permissionStatus) => {
                    this.cameraPermissionStatus = permissionStatus;
                    this.cameraPermissionStatus.onchange = () => {
                        configureDevices().then(()=> {
                            this.handleDeviceChanges();
                        }).catch((err)=> {
                            this.deviceError(err);
                        });
                    };
                });
                navigator.permissions.query({ name: 'microphone' }).then((permissionStatus) => {
                    this.micPermissionStatus = permissionStatus;
                    this.micPermissionStatus.onchange = () => {
                        configureDevices().then(()=> {
                            this.handleDeviceChanges();
                        }).catch((err)=> {
                            this.deviceError(err);
                        });
                    };
                });
            },
            async handleDeviceChanges(){
                const mediaDevices=await navigator.mediaDevices.enumerateDevices();
                let cameraDevice=mediaDevices.find(mediaDevice=>mediaDevice.deviceId===this.cameraDevice);
                let audioDevice=mediaDevices.find(mediaDevice=>mediaDevice.deviceId===this.audioInputDevice);

                if(cameraDevice && audioDevice && this.error && (this.error.title.includes('_disconnected_')||this.error.title.includes('device_'))){
                    this.onDeviceUpdated();
                    this.error=null;
                }

                if(this.active){
                    this.stopAndDiscard();
                }

                if(!cameraDevice){
                    this.error={
                        title:'components.video_record.webcam_disconnected_title',
                        description:'components.video_record.webcam_disconnected_desc',
                    };
                    this.$emit('error',this.error);
                    return;
                }

                if(!audioDevice){
                    this.error={
                        title:'components.video_record.audio_disconnected_title',
                        description:'components.video_record.audio_disconnected_desc',
                    };
                    this.$emit('error',this.error);
                    return;
                }

                this.error=null;
            },
            onDeviceUpdated(){
                this.checkDevicesInStore().then(async ()=>{
                    this.stopLocalVideoViewStream().then(()=>{
                        this.startLocalVideoViewStream().then(()=>{
                            this.stopAndDiscard();
                        });
                    })
                }).catch((deviceErr)=>{
                    this.deviceError(deviceErr);
                });
            },
            deviceError(deviceError){
                this.stopAndDiscard();
                if(deviceError.toString().includes('Permission denied')){
                    deviceError='permission';
                }else if(deviceError.toString().includes('NotReadable')){
                    deviceError='busy';
                }else if(deviceError.toString().includes('NotFound')){
                    deviceError='both';
                }
                this.error = {
                    title:'components.video_record.device_'+deviceError+'_error_title',
                    description:'components.video_record.device_'+deviceError+'_error_desc'
                };
                this.$emit('error',this.error);
            },
            startLocalVideoViewStream(){
                return navigator.mediaDevices.getUserMedia(this.settings).then((mediaStream) => {
                    this.$refs.video.srcObject = mediaStream;
                    this.stream = mediaStream;
                    this.tracks = mediaStream.getTracks();
                    this.startMicMonitor();
                    return this.tracks;
                }).catch((err) => {
                    this.deviceError(err);
                });
            },
            async stopLocalVideoViewStream(){
                const stream = this.$refs.video.srcObject;
                if (stream) {
                    const tracks = stream.getTracks();
                    tracks.forEach(function (track) {
                        track.stop();
                    });
                }
                this.$refs.video.srcObject = null;
            },
            async start(){
                this.error=null;
                if(!this.stream){
                    await this.startLocalVideoViewStream();
                }

                if(this.playSounds){
                    soundController.alert();
                }
                this.startCountdown();
            },
            stop(){
                this.stopCountdown();
                this.stopMicMonitor();
                this.stopLocalRecording().then((data)=>{
                    this.active=false;
                    this.$emit('inactive');
                    this.blobsToUpload = data;
                    this.uploadBlobs();
                }).catch((err)=>{
                    this.active=false;
                    this.$emit('inactive');
                    //TODO: What to do with other err states?
                    if(err==='too_short'){
                        this.reset();
                    }
                });
            },
            stopAndDiscard(){
                if(this.countdownInterval){
                    this.stopCountdown();
                }
                if(this.recordingInterval){
                    clearInterval(this.recordingInterval);
                }
                this.currentRecordingSeconds=0;
                this.$emit('reset');
                this.active=false;
                if(this.localAudioRecording){
                    this.stopRecordRTC(this.localAudioRecording).catch(()=>{});
                }
                if(this.localVideoRecording){
                    this.stopRecordRTC(this.localVideoRecording).catch(()=>{});
                }
                this.stopMicMonitor();
            },
            stopAndOpenSettings() {
                this.stopAndDiscard();
                this.$store.dispatch('settings/toggleSettingsDrawer', !this.$store.state.settings.isOpen);
            },
            startCountdown(){
                if(!this.stream) return;
                this.currentRecordingSeconds = 0;
                if(this.countdown===null){
                    this.onActive();
                }
                this.$emit('countdown-started',true);
                this.currentCountdown=this.countdown;

                if(this.playSounds) soundController.clink();

                this.countdownInterval = setInterval(()=>{
                    if(!this){
                        clearInterval(this.countdownInterval);
                    }

                    if(this.currentCountdown===this.$t('general.start')){
                        this.stopCountdown();
                        this.onActive();
                    }else if(this.currentCountdown===1){
                        if(this.playSounds) soundController.beep2();
                        this.currentCountdown=this.$t('general.start');
                    }else{
                        if(this.playSounds) soundController.clink();
                        this.currentCountdown--;
                    }
                },1000);
            },
            stopCountdown(){
                clearInterval(this.countdownInterval);
                this.currentCountdown=null;
                this.$emit('countdown-ended',false);
            },
            onActive(){
                if(this.active){
                    return;
                }
                this.startLocalRecording();
                this.active=true;
                this.$emit('active');
            },
            startLocalRecording() {
                this.blobsToUpload = null;
                this.canRetryUpload = false;
                this.currentRecordingSeconds = 0;
                this.localAudioRecording = RecordRTC(this.stream, this.audioRecordRTCSettings);
                this.localVideoRecording = RecordRTC(this.stream, this.videoRecordRTCSettings);
                this.localAudioRecording.startRecording();
                this.localVideoRecording.startRecording();
                this.recordingInterval = setInterval(() => {
                    this.currentRecordingSeconds++;
                }, 1000);
            },
            startMicMonitor() {
                micLevelCheck(this.audioInputDevice,(currentLevel, micIsLive)=>{
                    this.micLevel=currentLevel;
                    this.micIsActive=micIsLive;
                })
            },
            stopMicMonitor() {
                this.micLevel=0;
                this.micIsActive=true;
                stopMicLevelCheck();
            },
            stopLocalRecording() {
                clearInterval(this.recordingInterval);
                clearInterval(this.countdownInterval);
                return new Promise((resolve, reject) => {
                    this.stopRecordRTC(this.localAudioRecording).then((audioBlob) => {
                        this.stopRecordRTC(this.localVideoRecording).then((videoBlob) => {
                            resolve({audioBlob, videoBlob});
                        }).catch((err) => {
                            reject(err);
                        });
                    }).catch((err) => {
                        reject(err);
                    });
                });
            },
            stopRecordRTC(recordRTC){
                return new Promise((resolve, reject) => {
                    if(recordRTC && recordRTC.state==='recording'){
                        recordRTC.stopRecording(() => {
                            if(this.hasReachedMinimumRecordTime){
                                const blob = recordRTC.getBlob();
                                recordRTC.destroy();
                                recordRTC = null;
                                resolve(blob);
                            }else{
                                reject('too_short');
                            }
                        });
                    }else if(recordRTC){
                        recordRTC.destroy();
                        recordRTC = null;
                        reject('not_recording');
                    }else{
                        reject('no_object');
                    }
                });
            },
            uploadBlobs(){

                const blobs = this.blobsToUpload;

                if(!blobs || !blobs.audioBlob || !blobs.videoBlob){
                    this.uploading = false;
                    this.canRetryUpload = false;
                    this.uploadError = this.$t('components.video_record.confirm.errors.no_blobs');
                    return;
                }

                this.uploading = true;
                this.canRetryUpload=false;
                this.uploadError = null;
                this.videoUploadComplete = false;
                this.audioUploadComplete = false;
                this.uploadPercent = 0;
                this.responsevideo = null;
                this.videofile = null;

                this.$emit('uploading', true);
                const audioUrl = 'assessments/'+this.assessmentid+'/responsevideo/audio';
                const videoUrl = 'assessments/'+this.assessmentid+'/responsevideo/video';

                const progressHandler = (perc) => {
                    this.uploadPercent = perc;
                }

                this.uploadBlob(audioUrl, blobs.audioBlob, ()=>{}).then((response) => {
                    if(response.data.data && response.data.data.responsevideoid){
                        this.responsevideo=response.data.data;
                    }else{
                        const code = response.data.message;
                        if(code==='file_invalid' || code === 'content_invalid' || code === 'speech_invalid'){
                            this.uploadError = this.$t('components.video_record.confirm.errors.'+code);
                        }else{
                            this.uploadError = this.$t('components.video_record.confirm.errors.other');
                            this.canRetryUpload=true;
                        }
                    }
                    this.audioUploadComplete=true;
                    this.afterUpload();
                }).catch((err) => {
                    if(err.response.status===422){
                        this.uploading=false;
                        this.uploadError = this.$t('components.video_record.confirm.errors.validation_error');
                        this.canRetryUpload=true;
                    }else{
                        this.uploading=false;
                        this.uploadError = this.$t('components.video_record.confirm.errors.other');
                        this.canRetryUpload=true;
                        console.error(err)
                    }
                });

                this.uploadBlob(videoUrl, blobs.videoBlob, progressHandler).then((response) => {
                    this.videofile=response.data.data;
                    this.videoUploadComplete=true;
                    this.afterUpload();
                }).catch((err) => {
                    this.videoUploadComplete=true;
                    console.error(err)
                });
            },
            afterUpload() {
                if(!this.videoUploadComplete || !this.audioUploadComplete){
                    return;
                }

                if(this.videofile && this.responsevideo){
                    const url = 'assessments/'+this.assessmentid+'/responsevideos/'+this.responsevideo.responsevideoid+'/attachvideo';
                    this.$http.post(url,{fileid:this.videofile.fileid}).then((response)=>{
                        this.uploadSuccess = true;
                        this.uploading = false;
                        this.blobsToUpload = null;
                        this.responsevideo=response.data.data;
                        this.emitResponsevideo(this.responsevideo, 1500);
                    }).catch((err)=>{
                        console.error(err)
                        this.uploadSuccess = true;
                        this.blobsToUpload = null;
                        this.uploading = false;
                        this.emitResponsevideo(this.responsevideo, 1500);
                    });
                }else if(this.responsevideo){
                    this.uploadSuccess = true;
                    this.blobsToUpload = null;
                    this.uploading = false;
                    this.emitResponsevideo(this.responsevideo, 1500);
                }else if(this.uploadError){
                    this.uploading = false;
                    this.uploadSuccess = false;
                }else{
                    //TODO check this case
                    this.$emit('uploading', false);
                }
            },
            emitResponsevideo(responsevideo, delay=0) {
                setTimeout(()=>{
                    this.$emit('uploading', false);
                    this.$emit('responsevideo', responsevideo);
                },delay);
            },
            uploadBlob(url, blob, progressCallback){
                let file = new File([blob], 'record_' + uuidv4() + '.webm');
                const data = new FormData();
                data.append('file', file);
                data.append('assessmentanswerid', this.assessmentanswerid);
                return this.$http.post(url,data,{
                    onUploadProgress: (progress)=>{
                        const perc = progress.loaded / progress.total;
                        progressCallback(perc);
                    }
                })
            },
            reset(){
                if(this.countdownInterval){
                    clearInterval(this.countdownInterval);
                }

                if(this.recordingInterval){
                    clearInterval(this.recordingInterval);
                }

                this.active=false;
                this.canRetryUpload=false;
                this.uploading = false;
                this.blobsToUpload = null;
                this.uploadError = null;
                this.uploadSuccess = false;
                this.uploadPercent = 0;
                this.responsevideo = null;
                this.videofile = null;
                this.audioUploadComplete = false;
                this.videoUploadComplete = false;
                this.currentRecordingSeconds = 0;
                this.$emit('uploading', false);
                this.$emit('reset');
            },
            clearPermissionListeners(){
                if(this.micPermissionStatus){
                    this.micPermissionStatus.onchange = null;
                    this.micPermissionStatus=null;
                }
                if(this.cameraPermissionStatus){
                    this.cameraPermissionStatus.onchange = null;
                    this.cameraPermissionStatus=null;
                }
            },
            proctoringIssueTriggered(){
              this.stop();
            }
        },
        mounted() {
            this.listenForDeviceChanges();
            this.startLocalVideoViewStream();
            this.determineCodecSupport();
        },
        beforeDestroy(){
            clearInterval(this.recordingInterval);
            clearInterval(this.countdownInterval);
            this.stopLocalVideoViewStream();
            this.stopMicMonitor();
            navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeListener);
            this.clearPermissionListeners();
        }
    }
</script>

<style scoped>
    .webcam-container{
        position:relative;
        background-color: black;
        border-radius: 10px;
        box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
        overflow: hidden;
    }

    .webcam-container .webcam-window{
        position:absolute;
        left:0;
        top:0;
        width:100%;
        height:100%;
        border-radius: 10px;

    }

    .webcam-container .webcam-window.blur{
        -o-filter: blur(25px);
        filter: blur(25px);
    }

    .blur{
        -o-filter: blur(25px);
        filter: blur(25px);
    }

    .webcam-container .overlay-center{
        font-family: Roboto, sans-serif;
        font-size: 25pt;
        color: white;
        background-color: black;
        opacity: 0.3;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        width:100%;
        height:100%;
    }

    .centered{
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        width: 50px;
        height: 50px;
    }
</style>